import { Link as MuiLink } from '@mui/material';
import { Link as RouterLink } from '@tanstack/react-router';

const DEFAULT_BASE_PATH = '/management/emails-history/general';
const DEFAULT_HISTORY_ID_FIELD = 'LastOutboundHistoryId';

type FinanceEmailHistoryLinkParams = {
  basePath?: string;
  historyIdField?: string;
};

type Props = {
  dateValue: unknown;
  displayValue?: string;
  row?: Record<string, unknown>;
  rawParams?: string | null;
};

const parseParams = (rawParams?: string | null): FinanceEmailHistoryLinkParams => {
  if (!rawParams?.trim()) {
    return {};
  }

  try {
    return JSON.parse(rawParams) as FinanceEmailHistoryLinkParams;
  } catch {
    return {};
  }
};

const getRowValue = (row: Record<string, unknown> | undefined, field: string): unknown => {
  if (!row) {
    return undefined;
  }

  if (field in row) {
    return row[field];
  }

  const match = Object.keys(row).find((key) => key.toLowerCase() === field.toLowerCase());
  return match ? row[match] : undefined;
};

const toOutboundHistoryKey = (historyId: unknown): string | null => {
  if (historyId == null || historyId === '') {
    return null;
  }

  const raw = String(historyId).trim();
  if (/^o\d+$/i.test(raw)) {
    return raw.toLowerCase();
  }

  const numericId = Number(raw);
  if (!Number.isInteger(numericId) || numericId <= 0) {
    return null;
  }

  return `o${numericId}`;
};

const resolveLabel = (displayValue: string | undefined, dateValue: unknown): string | null => {
  const fromDisplay = displayValue?.trim();
  if (fromDisplay && fromDisplay !== '-') {
    return fromDisplay;
  }

  if (dateValue == null || dateValue === '') {
    return null;
  }

  return String(dateValue);
};

export const FinanceEmailHistoryLinkCell = ({ dateValue, displayValue, row, rawParams }: Props) => {
  const label = resolveLabel(displayValue, dateValue);
  if (!label) {
    return null;
  }

  const params = parseParams(rawParams);
  const historyIdField = params.historyIdField || DEFAULT_HISTORY_ID_FIELD;
  const historyKey = toOutboundHistoryKey(getRowValue(row, historyIdField));
  if (!historyKey) {
    return <>{label}</>;
  }

  const basePath = (params.basePath || DEFAULT_BASE_PATH).replace(/\/$/, '');
  const to = `${basePath}/${historyKey}`;

  return (
    <MuiLink
      component={RouterLink}
      to={to}
      underline="hover"
      onClick={(event) => event.stopPropagation()}
    >
      {label}
    </MuiLink>
  );
};
